import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  House, Stack, Tag, Scroll, Scissors,
  Warning, SignIn, SignOut, Buildings, MagnifyingGlass,
  Cube, Truck, ChartBar, Gear, Wrench, Bell, ClockCounterClockwise, QrCode, List, X, ClipboardText, Archive, Eye, EyeSlash, ListChecks, Broadcast,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import RecentCutsPanel from "@/components/RecentCutsPanel";
import { pageKeyForPath } from "@/lib/permissions";

// Authenticated nav grouped into sections
const sections = [
  {
    label: "Overview",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: House },
      { to: "/incoming-stock", label: "Incoming Stock", icon: Truck },
      { to: "/to-order", label: "Ordering", icon: ClipboardText },
    ],
  },
  {
    label: "Catalogue",
    items: [
      { to: "/manufacturers", label: "Manufacturers", icon: Buildings },
      { to: "/series", label: "Series", icon: Stack },
      { to: "/products", label: "Products", icon: Tag },
    ],
  },
  {
    label: "Inventory",
    items: [
      { to: "/rolls", label: "Rolls", icon: Scroll },
      { to: "/scan", label: "Scan", icon: QrCode },
      { to: "/per-unit-products", label: "Per Unit Products", icon: Wrench },
      { to: "/cuts", label: "Cuts", icon: Scissors },
      { to: "/stock-take", label: "Stock Take", icon: ListChecks },
      { to: "/alerts", label: "Alerts", icon: Warning },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/reports", label: "Analytics", icon: ChartBar },
      { to: "/archive", label: "Archive", icon: Archive },
      { to: "/product-radar", label: "Product Radar", icon: Broadcast },
      { to: "/update-requests", label: "Update Requests", icon: Bell },
      { to: "/activity-log", label: "Activity Log", icon: ClockCounterClockwise },
      { to: "/settings", label: "Settings", icon: Gear },
    ],
  },
];

export default function Layout() {
  const { user, isAuthed, logout, isAdmin, canAccessPage, canSeePricing, hideMoney, toggleHideMoney } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [importErrors, setImportErrors] = useState(0);
  const [orderCount, setOrderCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cutsOpen, setCutsOpen] = useState(false);
  const [cuts, setCuts] = useState([]);

  const loadCuts = () => api.get("/cuts/recent", { params: { limit: 40 } }).then(({ data }) => setCuts(data)).catch(() => {});

  const canOrder = isAuthed && canAccessPage("to-order");

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    const fetchCounts = () => {
      api.get("/change-requests/count")
        .then(({ data }) => { if (active) setPendingCount(data.pending || 0); })
        .catch(() => {});
      api.get("/import-errors/count")
        .then(({ data }) => { if (active) setImportErrors(data.unresolved || 0); })
        .catch(() => {});
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 20000);
    return () => { active = false; clearInterval(t); };
  }, [isAdmin, location.pathname]);

  useEffect(() => {
    if (!isAuthed || !canOrder) { setOrderCount(0); return; }
    let active = true;
    const fetchOrderCount = () => {
      api.get("/order-items/pending-count")
        .then(({ data }) => { if (active) setOrderCount(data.pending || 0); })
        .catch(() => {});
    };
    fetchOrderCount();
    const t = setInterval(fetchOrderCount, 20000);
    return () => { active = false; clearInterval(t); };
  }, [isAuthed, canOrder, location.pathname]);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-slate-900/40 z-40 lg:hidden" onClick={() => setMobileOpen(false)} data-testid="sidebar-backdrop" />
      )}
      {/* Sidebar */}
      <aside
        className={`w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col h-screen fixed lg:sticky top-0 left-0 z-50 transform transition-transform duration-200 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-slate-200 flex items-start justify-between gap-2">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-9 h-9 bg-slate-900 text-white grid place-items-center font-heading font-bold text-lg rounded-md">V</div>
            <div>
              <div className="font-heading font-bold text-slate-900 leading-tight">VWA Stock Manager</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Vinyl Wrapping Australia</div>
            </div>
          </Link>
          <button onClick={() => setMobileOpen(false)} className="lg:hidden p-1 -mr-1 text-slate-400 hover:text-slate-800" data-testid="sidebar-close-btn" aria-label="Close menu">
            <X size={22} />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-4 overflow-y-auto">
          {isAuthed && sections.map((section) => {
            const items = section.items.filter(({ to }) => canAccessPage(pageKeyForPath(to)));
            if (items.length === 0) return null;
            return (
              <div key={section.label}>
                <div className="px-3 mb-1.5 text-[9px] font-mono tracking-[0.25em] uppercase text-slate-400">{section.label}</div>
                <div className="space-y-0.5">
                  {items.map(({ to, label, icon: Icon, badge }) => (
                    <NavLink
                      key={to}
                      to={to}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all duration-150 ${
                          isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                        }`
                      }
                    >
                      <Icon size={18} />
                      <span className="flex-1">{label}</span>
                      {to === "/update-requests" && pendingCount > 0 && (
                        <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 bg-red-600 text-white rounded-full min-w-[20px] text-center" data-testid="update-requests-badge">
                          {pendingCount}
                        </span>
                      )}
                      {to === "/activity-log" && importErrors > 0 && (
                        <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 bg-red-600 text-white rounded-full min-w-[20px] text-center" data-testid="activity-log-badge">
                          {importErrors}
                        </span>
                      )}
                      {to === "/to-order" && orderCount > 0 && (
                        <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 bg-blue-600 text-white rounded-full min-w-[20px] text-center" data-testid="ordering-badge">
                          {orderCount}
                        </span>
                      )}
                      {badge && <span className="text-[9px] font-mono tracking-[0.15em] uppercase px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded-sm">{badge}</span>}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-200">
          {isAuthed ? (
            <div className="space-y-2">
              {canSeePricing && (
                <button
                  onClick={toggleHideMoney}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${hideMoney ? "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-100"}`}
                  data-testid="hide-money-toggle"
                  title={hideMoney ? "Money values are hidden — click to show" : "Hide all money values"}
                >
                  {hideMoney ? <EyeSlash size={16} /> : <Eye size={16} />}
                  <span className="flex-1 text-left">{hideMoney ? "Money hidden" : "Hide money"}</span>
                </button>
              )}
              <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md">
                <div className="text-xs font-mono text-slate-700 truncate">{user.email}</div>
                <div className={`text-[10px] tracking-[0.2em] uppercase mt-0.5 inline-block px-1.5 py-0.5 rounded-sm border ${
                  user.role === "admin" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  user.role === "viewer" ? "bg-purple-50 text-purple-700 border-purple-200" :
                  "bg-slate-100 text-slate-600 border-slate-200"
                }`}>
                  {user.role}
                </div>
              </div>
              <Button onClick={onLogout} variant="outline" className="w-full justify-start gap-2 rounded-md" data-testid="logout-btn">
                <SignOut size={16} /> Log out
              </Button>
            </div>
          ) : (
            <Button asChild className="w-full justify-start gap-2 bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="sidebar-login-btn">
              <Link to="/login">
                <SignIn size={16} /> Staff Login
              </Link>
            </Button>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className={`flex-1 min-w-0 flex flex-col min-h-screen transition-[margin] duration-200 ease-out ${cutsOpen ? "lg:mr-[260px]" : ""}`}>
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 bg-white border-b border-slate-200 px-4 h-14">
          <button onClick={() => setMobileOpen(true)} className="p-2 -ml-2 text-slate-700 hover:text-slate-900" data-testid="mobile-menu-btn" aria-label="Open menu">
            <List size={24} />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-slate-900 text-white grid place-items-center font-heading font-bold text-sm rounded-md">V</div>
            <span className="font-heading font-bold text-slate-900 text-sm">VWA Stock Manager</span>
          </Link>
        </header>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
      {isAuthed && <RecentCutsPanel open={cutsOpen} onOpenChange={setCutsOpen} cuts={cuts} onRefresh={loadCuts} />}
    </div>
  );
}
